import { Panel } from "../../components/Panel.tsx";

const STAGES = [
  ["NL box", "CodeMirror with lexicon + live, typo-tolerant Wikidata autocomplete."],
  ["Analyze", "Shallow templates detect the question shape (attribute-of, count, superlative, …)."],
  ["Link", "Ground mentions → real IRIs (curated lexicon first, then wbsearchentities). The brittle part."],
  ["IR", "A source-agnostic query graph — the contract every backend consumes."],
  ["Plan", "Which source(s) answer it. Single-source today; multi-source federation is the next seam."],
  ["Emit", "IR → Wikidata-dialect SPARQL (wd:/wdt:, label service, COUNT, limit-first subqueries)."],
  ["Execute + rank", "Run candidates; over-generate with runner-up entity/property links; keep what returns results."],
];

export function AboutPage() {
  return (
    <div className="space-y-5">
      <section className="pt-2">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--nl2s-text)" }}>
          How it works
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          nl2sparql is deterministic-first: a transparent pipeline, not a black box. An optional LLM
          can replace only the “understand the question” step — it emits the same structured match the
          templates do and flows through the identical grounding + validation, so it never writes raw
          SPARQL.
        </p>
      </section>

      <Panel title="The pipeline">
        <ol className="space-y-2">
          {STAGES.map(([name, desc], i) => (
            <li key={i} className="flex gap-3">
              <span
                className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold"
                style={{ background: "var(--nl2s-accent-soft)", color: "var(--nl2s-accent)" }}
              >
                {i + 1}
              </span>
              <span className="text-sm" style={{ color: "var(--nl2s-text)" }}>
                <span className="font-semibold">{name}</span>
                <span style={{ color: "var(--nl2s-text-muted)" }}> — {desc}</span>
              </span>
            </li>
          ))}
        </ol>
      </Panel>

      <Panel title="Why decouple with an IR?">
        <p className="text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          The classic failure of NL→SPARQL is coupling “what is the question asking” to “which endpoint
          answers it.” A source-agnostic Intermediate Representation breaks that coupling: Wikidata is
          just one backend that serializes the IR; an uploaded ontology is another source with its own
          index; and federation falls out of the per-term provenance the IR already carries. Everything
          you see in the Explain panel is that IR made visible.
        </p>
      </Panel>

      <Panel title="Milestones">
        <ul className="space-y-1.5 text-sm" style={{ color: "var(--nl2s-text)" }}>
          <li>
            <span className="font-semibold" style={{ color: "var(--nl2s-accent)" }}>M1 (now)</span>{" "}
            <span style={{ color: "var(--nl2s-text-muted)" }}>
              — Wikidata end-to-end, deterministic pipeline, explain + generated SPARQL, all seams in place.
            </span>
          </li>
          <li>
            <span className="font-semibold">M2</span>{" "}
            <span style={{ color: "var(--nl2s-text-muted)" }}>
              — upload your own ontology, guided disambiguation, editable SPARQL, saved history.
            </span>
          </li>
          <li>
            <span className="font-semibold">M3</span>{" "}
            <span style={{ color: "var(--nl2s-text-muted)" }}>
              — real federation across multiple endpoints/ontologies, stronger linking, optional LLM parser.
            </span>
          </li>
        </ul>
      </Panel>
    </div>
  );
}
